import { useSelector } from 'react-redux';
import { useMemo } from 'react';

/**
 * Hook to get install state from Redux
 * 
 * IMPORTANT: The install API is called only once when the store initializes.
 * If install data already exists in Redux, the API call is skipped automatically.
 * This hook provides optimized access to install state for all components.
 * 
 * @returns {Object} {
 *   installChecked: boolean,      // Whether install check has completed
 *   installSuccess: boolean,      // Whether install was successful
 *   installLoading: boolean,       // Whether install check is in progress
 *   installError: string | null,   // Error message if install failed
 *   installData: any,             // Install data from API (shop_info, etc.)
 *   shop: string | null,          // Shop domain
 *   host: string | null,          // App host parameter
 *   isReady: boolean              // True if app is ready to use (has install data)
 * }
 * 
 * @example
 * ```jsx
 * function MyComponent() {
 *   const { isReady, installData, installLoading } = useEnsureInstall();
 *   
 *   if (installLoading) return <Spinner />;
 *   if (!isReady) return <div>Not ready</div>;
 *   
 *   const clientId = installData?.shop_info?.id;
 *   return <div>Client ID: {clientId}</div>;
 * }
 * ```
 */
export function useEnsureInstall() {
  // Use selector to get install state from Redux
  // This is memoized by Redux, so components only re-render when install state changes
  const installState = useSelector((state) => state.install);

  // Memoize the isReady calculation to prevent unnecessary recalculations
  const isReady = useMemo(() => {
    return installState.checked && 
           installState.installData !== null && 
           (installState.success || (installState.shop !== null && installState.shop !== undefined));
  }, [installState.checked, installState.installData, installState.success, installState.shop]);

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(() => ({
    installChecked: installState.checked,
    installSuccess: installState.success,
    installLoading: installState.loading,
    installError: installState.error,
    installData: installState.installData,
    shop: installState.shop,
    host: installState.host,
    isReady
  }), [
    installState.checked,
    installState.success,
    installState.loading,
    installState.error,
    installState.installData,
    installState.shop,
    installState.host,
    isReady
  ]);
}
